import moment from 'moment'

export default {
  props: {
    year: {
      type: Number,
      default: () => moment().year()
    },
    startDate: {
      type: String,
      default: null
    },
    endDate: {
      type: String,
      default: null
    },
    periodType: {
      type: String,
      default: 'year',
      validator: value => ['isoWeek', 'month', 'year'].includes(value)
    }
  },
  computed: {
    isCurrentYear () {
      return this.year === moment().year()
    },
    hasPeriodRange () {
      return !!(this.startDate && this.endDate)
    },
    periodUnit () {
      return this.periodType === 'isoWeek' ? 'isoWeek' : this.periodType
    },
    sparklineUnit () {
      if (this.periodType === 'isoWeek') return 'day'
      if (this.periodType === 'month') return 'isoWeek'
      return 'month'
    },
    yearStartDate () {
      return moment()
        .year(this.year)
        .startOf('year')
    },
    yearEndDate () {
      if (this.isCurrentYear) return moment()
      return moment()
        .year(this.year)
        .endOf('year')
    },
    yearStartFormatted () {
      return this.yearStartDate.format('MM/DD/YYYY')
    },
    yearEndFormatted () {
      return this.yearEndDate.format('MM/DD/YYYY')
    },
    /** Active reporting window (selected period or full year). */
    rangeStartDate () {
      return this.hasPeriodRange ? moment(this.startDate) : this.yearStartDate
    },
    rangeEndDate () {
      return this.hasPeriodRange ? moment(this.endDate) : this.yearEndDate
    },
    rangeStartFormatted () {
      return this.rangeStartDate.format('MM/DD/YYYY')
    },
    rangeEndFormatted () {
      return this.rangeEndDate.format('MM/DD/YYYY')
    },
    /** Previous equivalent period for trend comparison. */
    prevRangeStartDate () {
      if (this.hasPeriodRange) {
        return moment(this.rangeStartDate)
          .subtract(1, this.periodUnit)
          .startOf(this.periodUnit)
      }
      return this.prevYearStartDate
    },
    prevRangeEndDate () {
      if (this.hasPeriodRange) {
        if (this.periodType === 'year' && this.isCurrentYear) {
          return moment().subtract(1, 'year')
        }
        return moment(this.prevRangeStartDate).endOf(this.periodUnit)
      }
      return this.prevYearEndDate
    },
    prevRangeStartFormatted () {
      return this.prevRangeStartDate.format('MM/DD/YYYY')
    },
    prevRangeEndFormatted () {
      return this.prevRangeEndDate.format('MM/DD/YYYY')
    },
    /** Wider window used for sparklines (keeps charts readable on short periods). */
    sparklineStartDate () {
      if (this.periodType === 'isoWeek') {
        return moment(this.rangeEndDate)
          .subtract(7, 'weeks')
          .startOf('isoWeek')
      }
      if (this.periodType === 'month') {
        return moment(this.rangeEndDate)
          .subtract(5, 'months')
          .startOf('month')
      }
      return this.rangeStartDate
    },
    sparklineStartFormatted () {
      return this.sparklineStartDate.format('MM/DD/YYYY')
    },
    trendLabelKey () {
      if (this.periodType === 'isoWeek') return 'stats.sales.since_last_week'
      if (this.periodType === 'month') return 'stats.sales.since_last_month'
      return 'stats.sales.vs_last_year'
    },
    /** Previous year, same YTD window as the selected year. */
    prevYearStartDate () {
      return moment()
        .year(this.year - 1)
        .startOf('year')
    },
    prevYearEndDate () {
      if (this.isCurrentYear) return moment().subtract(1, 'year')
      return moment()
        .year(this.year - 1)
        .endOf('year')
    },
    prevYearStartFormatted () {
      return this.prevYearStartDate.format('MM/DD/YYYY')
    },
    prevYearEndFormatted () {
      return this.prevYearEndDate.format('MM/DD/YYYY')
    }
  },
  methods: {
    formatTrendPercent (value) {
      const num = Number(value) || 0
      const sign = num > 0 ? '+' : ''
      return `${sign}${num}%`
    },
    trendIsUp (value) {
      return Number(value) > 0
    },
    trendIsDown (value) {
      return Number(value) < 0
    },
    sparklineColor (value) {
      if (this.trendIsDown(value)) return '#dc2626'
      if (this.trendIsUp(value)) return '#16a34a'
      return '#64748b'
    },
    percentChange (current, previous) {
      const cur = Number(current) || 0
      const prev = Number(previous) || 0
      if (!prev) return cur > 0 ? 100 : 0
      return (cur / prev - 1) * 100
    },
    sumInRange (rows, valueKey, start, end) {
      return rows
        .filter(row => {
          const d = moment(row.start_date)
          return d.isSameOrAfter(start) && d.isSameOrBefore(end)
        })
        .reduce((acc, row) => acc + (Number(row[valueKey]) || 0), 0)
    },
    filterInRange (rows, start, end) {
      return (rows || []).filter(row => {
        const d = moment(row.start_date)
        return d.isSameOrAfter(start) && d.isSameOrBefore(end)
      })
    }
  }
}
